<template>
    <div class="company-summary">
        <AonDrawer
            class="drawer-right"
            width="500px"
            right
            :show-close="false"
            :drawer-open="drawerOpen"
            @input="drawerOpen = false"
        >
            <div class="company-summary__content">
                <div v-if="loading" class="my-xl">
                    <AonSpinner class="mx-auto" :scale="0.5" />
                </div>
                <template v-if="!loading && currentCompany">
                    <div
                        class="company-summary__header display-flex justify-between align-items-center mb-md"
                    >
                        <p class="text-sm">
                            {{
                                currentCompany
                                    ? t('entitySummary.companySummary')
                                    : t('marketSummary.marketSummary')
                            }}
                        </p>
                        <AonButton
                            class="company-summary__close"
                            type="icon"
                            :style="{ color: '#000' }"
                            :icon-options="{ iconStyle: 'fas', iconName: 'fa-xmark' }"
                            @clicked="drawerOpen = false"
                        />
                    </div>

                    <!-- Company Summary -->
                    <div class="company-summary__entity" v-if="currentCompany">
                        <EntityTitle class="mb-xl" :entity="currentCompany" />
                        <Description
                            :title="currentCompany.name"
                            :description="currentCompany.description"
                            :dark="false"
                        />
                        <div
                            class="company-summary__stats display-flex justify-between flex-wrap my-xl"
                        >
                            <div
                                v-if="currentCompany.entity_type"
                                class="display-flex w-half mb-sm"
                            >
                                <font-awesome-icon icon="fas fa-flag" class="fa-fw mr-sm" />
                                <p class="small text-dark">
                                    {{ currentCompany.entity_type }}
                                </p>
                            </div>
                            <div
                                v-if="currentCompany.address"
                                class="display-flex w-half mb-sm display-flex pl-xs"
                            >
                                <font-awesome-icon
                                    icon="fas fa-map-marker-alt"
                                    class="fa-fw mr-sm"
                                />
                                <p class="small text-dark">
                                    {{ currentCompany.address.replace(/,/g, ', ') }}
                                </p>
                            </div>
                            <a
                                v-if="currentCompany.homepage_url"
                                :href="currentCompany.homepage_url"
                                target="_blank"
                                class="text-dark display-flex w-half mb-sm"
                            >
                                <font-awesome-icon
                                    icon="fas fa-external-link"
                                    class="fa-fw mr-sm"
                                />
                                <p class="small">{{ currentCompany.homepage_url }}</p>
                            </a>
                            <div v-if="totalRevenue" class="display-flex w-half mb-sm">
                                <font-awesome-icon icon="fas fa-sack-dollar" class="fa-fw mr-sm" />
                                <p class="small text-dark">{{ totalRevenue }} {{ revenueYear }}</p>
                            </div>
                        </div>
                        <div class="company-summary__actions display-flex justify-between mb-xl">
                            <AonButton
                                class="display-flex w-half mr-xl"
                                type="prominent"
                                :label="$t('marketSummary.explore')"
                                @clicked="exploreCompany(currentCompany)"
                            />
                            <AddToWatchlistButton
                                class="display-flex w-half"
                                id-type="Entity"
                                :id-values="[currentCompany.aon_entity_pk]"
                                style="min-width: 0"
                            />
                        </div>
                    </div>
                </template>
            </div>
        </AonDrawer>
    </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useEntityStore } from '@/stores'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

import { AonDrawer } from '@moatmetrics/armory/src/components'
import Description from '@/components/Description'
import AddToWatchlistButton from '../Watchlists/WatchlistActions/AddToWatchlistButton'
import EntityTitle from '@/components/company/EntityTitle'

import NumberAbbreviate from 'number-abbreviate'
import moment from 'moment'

const entityStore = useEntityStore()
const router = useRouter()

const { t } = useI18n()
const numberAbbreviate = new NumberAbbreviate(['K', 'M', 'B', 'T'])

// component logic
const drawerOpen = ref(false)
const loading = ref(false)

watch(
    () => drawerOpen.value,
    async (newVal, oldVal) => {
        if (!newVal) {
            entityStore.summaryEntity = null
        }
    }
)

watch(
    () => entityStore.summaryEntity,
    async (newVal, oldVal) => {
        if (!newVal) {
            drawerOpen.value = false
        }
        if (newVal && newVal !== oldVal) {
            drawerOpen.value = true
        }
    }
)

const currentCompany = computed(() => {
    return entityStore.summaryEntity
})

const totalRevenue = computed(() => {
    return !currentCompany.value || !currentCompany.value.total_revenue
        ? null
        : `$${numberAbbreviate.abbreviate(currentCompany.value.total_revenue)}`
})

const revenueYear = computed(() => {
    if (!currentCompany.value) {
        return null
    }
    return `(${moment.utc(currentCompany.value.revenue_end_date).format('YYYY')})`
})

const exploreCompany = (company) => {
    drawerOpen.value = false
    if (company) {
        router.push({
            name: 'Research Company',
            params: { targetPk: company.aon_entity_pk },
            query: { tabName: 'profile' },
        })
    }
}
</script>

<style lang="scss" scoped>
.company-summary {
    z-index: 3;

    :deep(.table-aips-latest) {
        border: none !important;

        .overflow-x-scroll,
        .aips-table,
        .aips-table-wrap {
            transform: none !important;
            border: none !important;
            padding: 0;
        }

        .aips-table {
            table-layout: auto !important;
        }

        thead tr {
            border: none !important;

            th {
                border: none !important;
                padding: var(--spacing-sm) !important;
            }
        }

        tbody tr td {
            padding: var(--spacing-sm) !important;
            border: none !important;
        }

        .table-header {
            display: none;
        }
    }

    :deep(.drawer) {
        .drawer-mask {
            height: calc(100% + 111px);
        }
    }
}
</style>
